import moment from 'moment';

import {
  CANONICAL_FORMAT,
  DATETIME_FORMATS,
  USER_EDIT_DATE_FORMAT,
  USER_EDIT_DATETIME_FORMAT,
} from 'util/DateConstants';
import {getLocaleInputFormats} from 'util/DateUtil';
import lruMemoize from 'util/lruMemoize';
import {Optional} from 'util/Types';

// The full set of formats we accept when parsing a free-text date/datetime: locale-aware date/datetime
// layouts (month/day order keyed off languageTag) plus the canonical machine formats. Shared so every text
// date parser accepts exactly the same inputs.
function localeDatetimeInputFormats(languageTag?: Optional<string>): Array<string> {
  return [...getLocaleInputFormats(/* includeTime */ true, languageTag, true), ...DATETIME_FORMATS];
}

function _tryParseAsMoment(text: string, languageTag?: Optional<string>): moment.Moment | null {
  const parsed = moment(text.trim(), localeDatetimeInputFormats(languageTag), /* strict */ true);
  return parsed.isValid() ? parsed : null;
}

/**
 * Parse a free-text date/datetime while preserving an explicit UTC offset when the text carries one. Unlike
 * tryParseAsMoment (which uses moment() and so reinterprets the instant in the local timezone), an
 * offset-bearing input is re-parsed with parseZone so its authored offset survives. `hasOffset` reports
 * whether an explicit offset was present, since a Moment alone can't distinguish "no offset given" from "an
 * explicit +00:00" — both read back as utcOffset 0. Returns null if the text isn't a recognized date.
 */
function _tryParseAsZonedMoment(
  text: string,
  languageTag?: Optional<string>
): {moment: moment.Moment; hasOffset: boolean} | null {
  const formats = localeDatetimeInputFormats(languageTag);
  const trimmed = text.trim();
  const local = moment(trimmed, formats, /* strict */ true);
  if (!local.isValid()) {
    return null;
  }
  const matchedFormat = local.creationData().format;
  const hasOffset = typeof matchedFormat === 'string' && matchedFormat.includes('Z');
  // Re-parse with parseZone when an offset is present so it is preserved rather than reinterpreted in the
  // local timezone. Offset-less inputs keep the plain parse: parseZone would assign the local offset and can
  // even shift the displayed wall clock for zones with historical (LMT) offsets.
  return {moment: hasOffset ? moment.parseZone(trimmed, formats, /* strict */ true) : local, hasOffset};
}

export const tryParseAsZonedMoment = lruMemoize(
  _tryParseAsZonedMoment,
  {max: 2000},
  (text: string, languageTag?: Optional<string>) => `${text};${languageTag}`
);

export const tryParseAsMoment = lruMemoize(
  _tryParseAsMoment,
  {max: 2000},
  (text: string, languageTag?: Optional<string>) => `${text};${languageTag}`
);

function _tryParse(text: string, languageTag?: Optional<string>): string | null {
  const parsed = _tryParseAsMoment(text, languageTag);
  return parsed ? parsed.format(CANONICAL_FORMAT) : null;
}

export const tryParse = lruMemoize(
  _tryParse,
  {max: 2000},
  (text: string, languageTag?: Optional<string>) => `${text};${languageTag}`
);

/**
 * @param {string} text: Representation of date-time value
 * @param {string} languageTag: Locale for date-time string value
 * @returns String representation for date value (with time value stripped)
 */
function _tryParseDatetimeAsDate(text: string, languageTag?: Optional<string>): string | null {
  const parsed = _tryParseAsMoment(text, languageTag);
  return parsed ? parsed.format(USER_EDIT_DATE_FORMAT) : null;
}

export const tryParseDatetimeAsDate = lruMemoize(
  _tryParseDatetimeAsDate,
  {max: 2000},
  (text: string, languageTag?: Optional<string>) => `${text};${languageTag}`
);

export function now(): string {
  return moment().format(USER_EDIT_DATETIME_FORMAT);
}
